import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './processOption.api';
import {
  GET_PROCESS_OPTIONS,
  GetProcessOptionAction,
  GO_TO_PROCESS_OPTIONS_CREATE,
  GO_TO_PROCESS_OPTIONS_LIST,
  GO_TO_PROCESS_OPTIONS_EDIT,
  GO_TO_PROCESS_OPTIONS_VIEW,
  GET_PROCESS_OPTIONS_LIST,
  SELECT_PROCESS_OPTIONS,
  ProcessOption,
  CreateProcessOptionsThunkAction,
  UpdateProcessOptionsThunkAction,
  DeleteProcessOptionsThunkAction,
  CREATE_PROCESS_OPTIONS,
  UPDATE_PROCESS_OPTIONS,
  DELETE_PROCESS_OPTIONS,
  ProcessOptionActionTypes,
  SelectProcessOptionAction,
} from './processOption.types';
import { getSelectedProcessOptions } from './processOption.selectors';
import { PROCESS_OPTIONS_ROUTE, SYSTEM_CONFIGURATION_ROUTE, SYSTEM } from '../../constants';

export function getProcessOptions(
  deliveryFunctionId: string, id: string,
): ProcessOptionActionTypes {
  return {
    type: GET_PROCESS_OPTIONS,
    payload: api.getProcessOptions(deliveryFunctionId, id),
  };
}

export function goToProcessOptionsCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_PROCESS_OPTIONS_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PROCESS_OPTIONS_ROUTE}/create`);
  };
}

export function goToProcessOptionsList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_PROCESS_OPTIONS_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PROCESS_OPTIONS_ROUTE}`);
  };
}

export function getProcessOptionsList(deliveryFunctionId: string): ProcessOptionActionTypes {
  return {
    type: GET_PROCESS_OPTIONS_LIST,
    payload: api.getProcessOptionsList(deliveryFunctionId),
  };
}

export function createProcessOptions(
  deliveryFunctionId: string,
  body: WithAuditDetails<ProcessOption>,
  history: History,
): CreateProcessOptionsThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_PROCESS_OPTIONS,
        payload: api.createProcessOptions(deliveryFunctionId, body),
      });

      dispatch(getProcessOptionsList(deliveryFunctionId));
      dispatch(goToProcessOptionsList(history));
      showSuccessMessage(i18n.t('processOptions.create.success', { name: body.data.PROCNAME }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('processOptions.create.failed', { name: body.data.PROCNAME, error: errorMessage }));
    }
  };
}

export function goToProcessOptionsEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_PROCESS_OPTIONS_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PROCESS_OPTIONS_ROUTE}/edit`);
  };
}

export function goToProcessOptionsView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_PROCESS_OPTIONS_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PROCESS_OPTIONS_ROUTE}/view`);
  };
}

export function selectProcessOptions(
  processOption: ProcessOption[],
): SelectProcessOptionAction {
  return {
    type: SELECT_PROCESS_OPTIONS,
    payload: processOption,
  };
}

export function updateProcessOptions(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<ProcessOption>,
  history: History,
): UpdateProcessOptionsThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_PROCESS_OPTIONS,
        payload: api.updateProcessOptions(deliveryFunctionId, id, body),
      });

      dispatch(getProcessOptionsList(deliveryFunctionId));
      dispatch(goToProcessOptionsList(history));
      showSuccessMessage(i18n.t('processOptions.update.success', { name: body.data.PROCNAME }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('processOptions.update.failed', { name: body.data.PROCNAME, error: errorMessage }));
    }
  };
}

export function deleteProcessOptions(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteProcessOptionsThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedProcessOptions] = getSelectedProcessOptions(getState());

    try {
      await dispatch({
        type: DELETE_PROCESS_OPTIONS,
        payload: api.deleteProcessOptions(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('dialedDigitExtractionServer.delete.success'), 5000);
      if (history != null) {
        dispatch(goToProcessOptionsList(history));
      }
    } catch (e) {
      showError(i18n.t('dialedDigitExtractionServer.delete.failed'));

    } finally {
      dispatch(getProcessOptionsList(deliveryFunctionId));
    }
  };
}
